export type LogLevel = "debug" | "info" | "warn" | "error" | "none";

export const DEFAULT_API_BASE_URL =
  "https://dev06-template-builder-api.optum.com";
const DEFAULT_NOTIFICATION_ENV = "dev";
export const DEFAULT_IDLE_TIMEOUT_MINUTES = 30;
export const DEFAULT_API_REQUEST_TIMEOUT_MS = 30_000;
export const DEFAULT_RP_MAX_VARIABLES = 5;
export const DEFAULT_MAX_ALLOW_VARIABLE = 25;

export function requireRuntimeValue(
  name: string,
  value: string | undefined,
): string {
  const trimmedValue = value?.trim();
  if (!trimmedValue) {
    throw new Error(`Missing required environment variable: ${name}`);
  }
  return trimmedValue;
}

export function parseLogLevel(value: string | undefined): LogLevel | undefined {
  if (
    value === "debug" ||
    value === "info" ||
    value === "warn" ||
    value === "error" ||
    value === "none"
  ) {
    return value;
  }
  return undefined;
}

export function parseIdleTimeoutMs(value: string | undefined): number {
  const minutes = Number(value);
  return (minutes || DEFAULT_IDLE_TIMEOUT_MINUTES) * 60 * 1000;
}

export function parseRequestTimeoutMs(value: string | undefined): number {
  const timeoutMs = Number(value);
  return Number.isFinite(timeoutMs) && timeoutMs > 0
    ? timeoutMs
    : DEFAULT_API_REQUEST_TIMEOUT_MS;
}

export function parseRpMaxVariables(value: string | undefined): number {
  const limit = Number(value);
  return Number.isInteger(limit) && limit >= 0
    ? limit
    : DEFAULT_RP_MAX_VARIABLES;
}

export function parseMaxAllowVariable(value: string | undefined): number {
  const limit = Number(value);
  return Number.isInteger(limit) && limit >= 0
    ? limit
    : DEFAULT_MAX_ALLOW_VARIABLE;
}

export function parseBooleanFlag(value: string | undefined): boolean {
  return value === "true";
}

export function parseHarmonyImagePickerEnabled(
  value: string | undefined,
): boolean {
  return value?.trim().toLowerCase() !== "false";
}

export const runtimeConfig = {
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL?.trim() || undefined,
  oidcAuthority: import.meta.env.VITE_OIDC_AUTHORITY?.trim() || undefined,
  oidcClientId: import.meta.env.VITE_OIDC_CLIENT_ID?.trim() || undefined,
  harmonyAppId: import.meta.env.VITE_HARMONY_APP_ID?.trim() || undefined,
  harmonyLicenseKey:
    import.meta.env.VITE_HARMONY_LICENSE_KEY?.trim() || undefined,
  environment: import.meta.env.VITE_ENV?.trim() || DEFAULT_NOTIFICATION_ENV,
  logLevel: parseLogLevel(import.meta.env.VITE_LOG_LEVEL),
  idleTimeoutMs: parseIdleTimeoutMs(import.meta.env.VITE_IDLE_TIMEOUT_MINUTE),
  apiRequestTimeoutMs: parseRequestTimeoutMs(
    import.meta.env.VITE_API_REQUEST_TIMEOUT_MS,
  ),
  maxAllowVariable: parseMaxAllowVariable(
    import.meta.env.VITE_MAX_ALLOW_VARIABLE,
  ),
  rpMaxVariables: parseRpMaxVariables(import.meta.env.VITE_RP_MAX_VARIABLES),
  toastNotificationsEnabled: import.meta.env.VITE_TOAST_NOTIFICATIONS_ENABLED,
  useHarmonyImagePicker: parseHarmonyImagePickerEnabled(
    import.meta.env.VITE_USE_HARMONY_IMAGE_PICKER,
  ),
};
